import pickBy from 'lodash/pickBy'
import some from 'lodash/some'
import {
  GeckoConeCircumferentialWeld,
  GeckoConeCircumferentialWeldJSON,
  GeckoConeRadialWeld,
  GeckoConeRadialWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
} from 'types/assetModel/commonFields'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoConeCourse,
  GeckoConeCourseJSON,
} from 'types/assetModel/features/legacyCone/geckoConeCourse'
import {
  GeckoConicalPlate,
  GeckoConicalPlateJSON,
} from 'types/assetModel/features/legacyCone/geckoConicalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'
import { v4 as uuid4 } from 'uuid'

// TODO: ENT-1465 Add types to features once cones support it
export type LegacyConeAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.LegacyCone
  outer_radius: number
  installation_date: string | undefined
  scene_config: Record<string, unknown>
  features: Record<
    string,
    | GeckoConicalPlateJSON
    | GeckoConeCourseJSON
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
  >
  component_bounds: {
    y_start: number
    y_end: number
  }
  compass_orientation: number | undefined
}

export const LegacyConeAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.ConeCourse,
  GeckoFeatureModelType.ConePlate,
  GeckoFeatureModelType.ConeCircumferentialWeld,
  GeckoFeatureModelType.ConeRadialWeld,
]

export const NewLegacyConeAssetModelJSON = {
  name: 'New Legacy Cone',
  type: GeckoComponentModelType.LegacyCone,
  outer_radius: 0,
  installation_date: undefined,
  scene_config: {},
  component_bounds: {
    y_start: 0,
    y_end: 0,
  },
  features: {},
  compass_orientation: undefined,
} as LegacyConeAssetModelJSON

export class GeckoLegacyCone extends GeckoEditableComponent {
  type: GeckoComponentModelType.LegacyCone
  outerRadius: GeckoMeasurement
  installationDate: Date | undefined
  sceneConfig = undefined
  componentBounds: GeckoComponentRect
  courses: Map<string, GeckoConeCourse>
  plates: Map<string, GeckoConicalPlate>
  coneCircumferentialWelds: Map<string, GeckoConeCircumferentialWeld>
  coneRadialWelds: Map<string, GeckoConeRadialWeld>
  compassOrientation: number | undefined
  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    outerRadius: GeckoMeasurement,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    courses: Map<string, GeckoConeCourse>,
    plates: Map<string, GeckoConicalPlate>,
    coneCircumferentialWelds: Map<string, GeckoConeCircumferentialWeld>,
    coneRadialWelds: Map<string, GeckoConeRadialWeld>,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.LegacyCone
    this.outerRadius = outerRadius
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.courses = courses
    this.plates = plates
    this.coneCircumferentialWelds = coneCircumferentialWelds
    this.coneRadialWelds = coneRadialWelds
    this.compassOrientation = compassOrientation
  }

  public static CreateFromJSON(id: string, json: LegacyConeAssetModelJSON) {
    const name = json.name as string
    const outerRadius = GeckoMeasurement.CreateFromValue(
      json.outer_radius as number,
      GeckoMeasurementUnit.Meters
    )
    const installationDate = parseDate(json.installation_date)

    const featureData = json.features as Record<
      string,
      | GeckoConicalPlateJSON
      | GeckoConeCircumferentialWeldJSON
      | GeckoConeRadialWeldJSON
      | GeckoConeCourseJSON
    >

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      0,
      componentBoundsData.y_start,
      360,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Degrees,
      GeckoMeasurementUnit.Meters
    )

    const childCourseJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConeCourse
    })
    const courses = GeckoConeCourse.CreateMapFromJSON(
      childCourseJSON as Record<string, GeckoConeCourseJSON>,
      componentBounds,
      GeckoMeasurementUnit.Meters
    )
    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConePlate
    })
    const plates = GeckoConicalPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoConicalPlateJSON>
    )
    const childConeCircumferentialWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConeCircumferentialWeld
    })
    const coneCircumferentialWelds =
      GeckoConeCircumferentialWeld.CreateMapFromJSON(
        id,
        childConeCircumferentialWeldJSON as Record<
          string,
          GeckoConeCircumferentialWeldJSON
        >
      )

    const childConeRadialWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ConeRadialWeld
    })
    const coneRadialWelds = GeckoConeRadialWeld.CreateMapFromJSON(
      id,
      childConeRadialWeldJSON as Record<string, GeckoConeRadialWeldJSON>
    )
    const compassOrientation = parseNumber(json.compass_orientation)
    return new GeckoLegacyCone(
      id,
      json,
      name,
      outerRadius,
      installationDate,
      componentBounds,
      courses,
      plates,
      coneCircumferentialWelds,
      coneRadialWelds,
      compassOrientation
    )
  }

  public get coursesArray() {
    return Array.from(this.courses.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get coneRadialWeldsArray() {
    return Array.from(this.coneRadialWelds.values())
  }

  public get coneCircumferentialWeldsArray() {
    return Array.from(this.coneCircumferentialWelds.values())
  }

  public asJSON(): LegacyConeAssetModelJSON {
    return generateLegacyCone(
      this.name,
      this.outerRadius,
      this.installationDate,
      this.componentBounds.y.start,
      this.componentBounds.y.end,
      this.compassOrientation,
      this.json as LegacyConeAssetModelJSON
    )
  }
}

const generateLegacyCone = (
  name: string,
  outerRadius: GeckoMeasurement,
  installationDate: Date | undefined,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement,
  compassOrientation: number | undefined,
  otherData: LegacyConeAssetModelJSON = NewLegacyConeAssetModelJSON
): LegacyConeAssetModelJSON => {
  const outerRadiusMeters = outerRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .round(assetModelPrecision)
  const coneInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined
  const yStartMeters = yStart
    .convertTo(GeckoMeasurementUnit.Meters)
    .round(assetModelPrecision)
  const yEndMeters = yEnd
    .convertTo(GeckoMeasurementUnit.Meters)
    .round(assetModelPrecision)

  // ensure all cones have at least one course (used for nominal inputs)
  const hasCourse = some(
    Object.values(otherData.features),
    (feature) => feature.type === GeckoFeatureModelType.ConeCourse
  )
  const featureSetToUse: LegacyConeAssetModelJSON['features'] = {
    ...otherData.features,
    ...(hasCourse
      ? {}
      : {
          [`course-${uuid4().slice(0, 6)}`]: {
            type: GeckoFeatureModelType.ConeCourse,
            name: 'Course 1',
            feature_geometry: {
              y_end: outerRadiusMeters.num,
            },
            tmin: undefined,
            nominal: {
              wall_thickness: undefined,
              coating_thickness: undefined,
            },
          },
        }),
  }

  return {
    name: name,
    type: GeckoComponentModelType.LegacyCone,
    outer_radius: outerRadiusMeters.num,
    installation_date: coneInstallationDate,
    scene_config: otherData.scene_config,
    features: featureSetToUse,
    component_bounds: {
      y_start: yStartMeters.num,
      y_end: yEndMeters.num,
    },
    compass_orientation: compassOrientation,
  }
}

export const LegacyConeAssetModelFormFields = (
  JSON: LegacyConeAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Legacy Cone Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'outer_radius',
      label: 'Outer Diameter',
      value: JSON.outer_radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    installationDateFormField(JSON.installation_date, true),
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: JSON.component_bounds.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: JSON.component_bounds.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    compassOrientationFormField(JSON.compass_orientation),
  ]
}

export const LegacyConeJSONFromFormFields = (
  component: LegacyConeAssetModelJSON,
  formFields: FormField[]
) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generateLegacyCone(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'outer_radius')?.value as number) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    compassOrientation,
    component
  )
}
